import { useState, useMemo, useEffect, useRef } from "react";
import { useWindowKeydown } from "@/hooks/useWindowKeydown";
import { keyedEntries } from "@/lib/listIdentity";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Compass, DoorOpen, ChevronDown, ChevronUp, Package, Sparkles, Home, ArrowLeft, Coins, Gem, Sword, History, Skull, Trophy, Shield, Search, ZoomIn, ZoomOut, Heart, Wind, X, UserPlus, Users, Store, Hammer, HandCoins, Eye, KeyRound, Swords, ScrollText } from "lucide-react";
import { CardHoverPreview } from "@/components/Card3D";
import { ResistGrid, ELEMENT_META as SITE_ELEMENT_META } from "@/components/ArenaBits";
import { materialIcon } from "@/lib/materialIcons";
import { GearHoverCard } from "@/components/GearSlots";
import { InventoryIcon, InventoryName } from "@/components/InventoryIdentity";
import { inventoryVisual } from "@/lib/inventoryVisuals";

/**
 * /deep-dive — roguelike exploration mode (V.020 Phase 1 + 2).
 *
 * Renders a tile map for the player's current run. Movement is by clicking
 * an adjacent floor / door / stairs tile. Fog of war is enforced server-side;
 * anything the server marks as wall is drawn as unrevealed. Backtracking is
 * built in — walk back to the up-stairs to return to earlier floors.
 */
const TILE = { WALL: 0, FLOOR: 1, DOOR: 2, STAIRS_DOWN: 3, STAIRS_UP: 4, CHEST: 5, EXTRACT: 6, ENEMY: 7, NPC: 8, BOSS_DOOR: 9 };
const TILE_COLORS = {
  0: "#0a0f14",  // wall — barely visible, matches fog
  1: "#1e2933",  // floor
  2: "#7c5c2b",  // door
  3: "#2b6f7c",  // stairs down (teal)
  4: "#7c2b6f",  // stairs up (magenta)
  5: "#8a6a1c",  // chest
  6: "#3fb85e",  // extraction runeplate
  7: "#5a1a1a",  // enemy tile background — red-tinted floor
  8: "#3b2f5a",  // NPC tile background — muted violet
  9: "#facc15",  // boss door — bright amber to be unmistakable
};
const CELL = 22;
const CELL_MIN = 14;
const CELL_MAX = 44;

// Reuse the SITE-WIDE element meta from ArenaBits so resistance chips and
// element badges look identical everywhere. We wrap it to keep the local
// helpers (which expect `color`/`bg`) backward compatible.
const ELEMENT_META = Object.fromEntries(
  Object.entries(SITE_ELEMENT_META).map(([k, v]) => [k, {
    name: v.name,
    color: v.color,
    // 0.15 alpha tint of the element colour — matches the site's chip tone.
    bg: v.color + "26",
    Icon: v.Icon,
  }])
);
const RARITIES = ["common", "uncommon", "rare", "epic", "legendary"];

// Phase 6 — NPC role → icon + accent color so tokens are recognisable on the grid.
const NPC_ROLE_META = {
  trader:     { icon: HandCoins,  color: "#fbbf24", label: "Trader" },
  merchant:   { icon: Store,      color: "#facc15", label: "Merchant" },
  healer:     { icon: Heart,      color: "#f472b6", label: "Healer" },
  blacksmith: { icon: Hammer,     color: "#f97316", label: "Blacksmith" },
  challenger: { icon: Swords,     color: "#ef4444", label: "Challenger" },
  informant:  { icon: Eye,        color: "#22d3ee", label: "Informant" },
  gatekeeper: { icon: KeyRound,   color: "#a78bfa", label: "Gatekeeper" },
  questgiver: { icon: ScrollText, color: "#34d399", label: "Quest Giver" },
};

export default function DeepDivePage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  // Lift the run-end summary state to the page so it SURVIVES the RunView
  // unmount when the invalidated /current query flips `active` to false
  // (bug fixed in iteration 98).
  const [summary, setSummary] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ["deep-dive-current"],
    queryFn: async () => (await api.get("/deep-dive/current")).data,
    // V.027 perf pass — we mutate the cache directly on every action
    // response so /current never needs a refetch mid-run. Keeps WASD
    // inputs instant and eliminates the 100-200ms invalidate→refetch
    // round-trip that used to run after each move.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    enabled: !!user,
  });

  // Update-in-place helpers used by RunView + StartRunView.
  const setRun = (run) => qc.setQueryData(["deep-dive-current"], { active: !!run, run });
  const clearRun = () => qc.setQueryData(["deep-dive-current"], { active: false, run: null });

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="deep-dive-signin-gate">
        <Compass className="h-10 w-10 text-cyan-300 mx-auto mb-3" />
        <h1 className="font-display text-3xl text-white">Deep Dive</h1>
        <p className="mt-2 text-white/60">Log in to seed a run.</p>
        <Link to="/login" className="mt-4 inline-block">
          <Button className="bg-cyan-400 text-black hover:bg-cyan-300">Sign in</Button>
        </Link>
      </div>
    );
  }

  if (isLoading) return <div className="max-w-3xl mx-auto p-8 text-white/50" data-testid="deep-dive-loading">Loading…</div>;

  return (
    <>
      {!data?.active
        ? <StartRunView onStarted={setRun} />
        : <RunView run={data.run} setRun={setRun} clearRun={clearRun}
            onRunEnded={setSummary} />}
      {summary && (
        <RunSummaryModal
          summary={summary}
          card={summary.card}
          onClose={() => {
            setSummary(null);
            // Refresh history so the panel picks up the just-ended run.
            qc.invalidateQueries({ queryKey: ["deep-dive-history"] });
          }}
        />
      )}
    </>
  );
}

function StartRunView({ onStarted }) {
  const { data } = useQuery({
    queryKey: ["cards", "mine-for-dive"],
    queryFn: async () => (await api.get("/cards/mine")).data,
    select: (items) => items.filter((card) => !card.locked_out),
    staleTime: 15_000,
  });
  const { data: hist } = useQuery({
    queryKey: ["deep-dive-history"],
    queryFn: async () => (await api.get("/deep-dive/history?limit=10")).data,
    staleTime: 15_000,
  });
  const eligibleAll = (data || []).filter((c) => !c.locked_in_market && !c.locked_in_trade && !c.locked_in_battle);
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState("");
  const [rarity, setRarity] = useState("");
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 12;

  const cards = useMemo(() => {
    const query = q.trim().toLowerCase();
    return eligibleAll.filter((c) => {
      if (rarity && c.rarity !== rarity) return false;
      if (query && !c.title?.toLowerCase().includes(query)) return false;
      return true;
    });
  }, [eligibleAll, q, rarity]);

  const totalPages = Math.max(1, Math.ceil(cards.length / PAGE_SIZE));
  const pageCards = cards.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const selectedCard = selected ? eligibleAll.find((c) => c.id === selected) : null;

  const start = useMutation({
    mutationFn: async () => (await api.post("/deep-dive/start", { card_id: selected })).data,
    onSuccess: (run) => { toast.success("Descending…"); onStarted(run); },
    onError: (e) => toast.error(apiError(e, "Could not start")),
  });

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="deep-dive-start">
      <div className="flex items-center gap-2 mb-1">
        <Compass className="h-4 w-4 text-cyan-300" />
        <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-cyan-300">Deep Dive · Roguelike</span>
      </div>
      <h1 className="font-display text-4xl text-white tracking-[0.04em]">Choose your delve card</h1>
      <p className="mt-2 text-white/60 text-sm max-w-2xl">
        Your card becomes your token in the dungeon and its <strong className="text-white/85">real arena stats</strong> port in — HP, ATK, DEF, moves with cooldowns, mods, and resistances. If it falls, the run ends but the card always comes home unharmed and banked loot is delivered anyway.
      </p>

      {/* Filters */}
      <div className="mt-6 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/40" />
          <Input
            data-testid="deep-dive-search"
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(0); }}
            placeholder="Search titles…"
            className="pl-7 h-8 w-48 bg-black/40 border-white/15 text-white text-xs"
          />
        </div>
        <div className="flex items-center gap-1">
          {RARITIES.map((r) => (
            <button key={r} onClick={() => { setRarity(rarity === r ? "" : r); setPage(0); }}
              data-testid={`deep-dive-rarity-${r}`}
              className={`px-2 py-1 rounded-full border font-mono text-[9px] uppercase tracking-[0.24em] transition-colors ${
                rarity === r ? "border-cyan-300 bg-cyan-400/20 text-cyan-100" : "border-white/15 bg-black/30 text-white/60 hover:border-white/30"
              }`}>{r}</button>
          ))}
        </div>
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/40 ml-1" data-testid="deep-dive-card-count">
          {cards.length} eligible
        </span>
      </div>

      <div className="mt-4">
        {/* Card grid — focus mode when selected. Hover on any card uses the
            site-wide CardHoverPreview so the delve picker mirrors the rest
            of the site. */}
        <div>
          {cards.length === 0 ? (
            <div className="rounded-2xl border border-white/10 bg-black/30 p-6 text-white/50 text-sm">
              {eligibleAll.length === 0 ? "No eligible cards. Free up something locked in market or trade first." : "Nothing matches those filters."}
            </div>
          ) : selectedCard ? (
            // FOCUS mode — only the selected card, others hidden.
            <div className="rounded-2xl border border-cyan-300/40 bg-cyan-400/[0.05] p-4" data-testid="deep-dive-focus">
              <div className="flex items-center justify-between mb-3">
                <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-cyan-300">Delve token — locked in</span>
                <button onClick={() => setSelected(null)} data-testid="deep-dive-focus-clear"
                  className="text-white/50 hover:text-white text-xs inline-flex items-center gap-1">
                  <X className="h-3.5 w-3.5" /> Change card
                </button>
              </div>
              <FocusedCard card={selectedCard} />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {pageCards.map((c) => (
                  <CardHoverPreview key={c.id} card={c} side="right">
                    <button onClick={() => setSelected(c.id)}
                      data-testid={`deep-dive-card-${c.id}`}
                      className="w-full rounded-2xl border border-white/10 bg-black/30 hover:border-cyan-300/50 hover:bg-cyan-400/[0.05] transition-colors p-2 text-left group">
                      <div className="aspect-[3/4] rounded-lg overflow-hidden bg-black/40 mb-2 relative">
                        {c.front_image && <img src={c.front_image} alt={c.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform" />}
                        {c.arena?.hp && (
                          <div className="absolute bottom-1 right-1 rounded bg-black/70 px-1.5 py-0.5 font-mono text-[9px] text-red-200">
                            {c.arena.hp} HP
                          </div>
                        )}
                      </div>
                      <div className="text-white text-xs font-medium truncate">{c.title}</div>
                      <div className="flex items-center justify-between mt-0.5">
                        <span className="font-mono text-[9px] uppercase text-white/45">{c.rarity}</span>
                        {c.arena?.atk && (
                          <span className="font-mono text-[9px] text-white/60">
                            <Sword className="inline h-2.5 w-2.5" />{c.arena.atk} <Shield className="inline h-2.5 w-2.5 ml-0.5" />{c.arena.def}
                          </span>
                        )}
                      </div>
                    </button>
                  </CardHoverPreview>
                ))}
              </div>
              {totalPages > 1 && (
                <div className="mt-4 flex items-center justify-center gap-2" data-testid="deep-dive-pagination">
                  <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}
                    className="border-white/15 text-white/70">Prev</Button>
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/50">
                    Page {page + 1} / {totalPages}
                  </span>
                  <Button size="sm" variant="outline" disabled={page >= totalPages - 1} onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    className="border-white/15 text-white/70">Next</Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="mt-6">
        <Button disabled={!selected || start.isPending} onClick={() => start.mutate()}
          data-testid="deep-dive-start-button"
          size="lg"
          className="bg-cyan-400 text-black hover:bg-cyan-300">
          <Compass className="h-4 w-4 mr-1" /> {start.isPending ? "Seeding…" : selectedCard ? `Descend with ${selectedCard.title}` : "Descend"}
        </Button>
      </div>

      <RunHistoryPanel items={hist?.items || []} />
    </div>
  );
}

function FocusedCard({ card }) {
  return (
    <div className="grid gap-4 sm:grid-cols-[10rem_1fr]">
      <div className="aspect-[3/4] rounded-lg overflow-hidden bg-black/40 relative">
        {card.front_image && <img src={card.front_image} alt={card.title} className="w-full h-full object-cover" />}
      </div>
      <div>
        <div className="text-white text-lg font-semibold">{card.title}</div>
        <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-white/50 mt-0.5">
          {card.rarity} · {card.profession_name || "no profession"}
        </div>
        <CardStatsPanel card={card} />
      </div>
    </div>
  );
}

// Reusable panel — used in both the picker preview + inside the active run.
function CardStatsPanel({ card, compact = false, current = null, cooldowns = null }) {
  const a = card.arena || {};
  const moves = a.moves || [];
  const resist = a.resist || [];
  const mods = a.mods || {};
  // V.034 — `current` (optional) holds live in-run values so the panel can
  // show a delta underneath each stat: e.g., HP that dropped from 488 → 468
  // renders "468" with a small red "−20" below. Falls back to no-delta when
  // current isn't provided (Playden collection view, previews).
  // `cooldowns` (optional) is a { move_id: turns_left } map so the Moves list
  // can render a live cd chip to the LEFT of the ability name in place of the
  // static `cd${m.cd}` label.
  const cur = current || {};
  const val = (k) => (cur[k] != null ? cur[k] : a[k]);
  const delta = (k) => (cur[k] != null && a[k] != null) ? cur[k] - a[k] : 0;
  return (
    <div className={compact ? "rounded-2xl border border-white/10 bg-black/40 p-3" : "mt-2"} data-testid="card-stats-panel">
      <div className="grid grid-cols-4 gap-2 text-center">
        <StatCell label="HP"  value={val("hp")}  base={a.hp}  delta={delta("hp")}  icon={Heart}  color="text-red-300" />
        <StatCell label="ATK" value={val("atk")} base={a.atk} delta={delta("atk")} icon={Sword}  color="text-orange-300" />
        <StatCell label="DEF" value={val("def")} base={a.def} delta={delta("def")} icon={Shield} color="text-sky-300" />
        <StatCell label="SPD" value={val("spd")} base={a.spd} delta={delta("spd")} icon={Wind}   color="text-emerald-300" />
      </div>
      {(mods.crit || mods.dodge || mods.armor_pen || mods.lifesteal) > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {mods.crit > 0 && <ModChip label="Crit" pct={mods.crit} />}
          {mods.dodge > 0 && <ModChip label="Dodge" pct={mods.dodge} />}
          {mods.armor_pen > 0 && <ModChip label="Pen" pct={mods.armor_pen} />}
          {mods.lifesteal > 0 && <ModChip label="Lifesteal" pct={mods.lifesteal} />}
        </div>
      )}
      {moves.length > 0 && (
        <div className="mt-3">
          <div className="font-mono text-[9px] uppercase tracking-[0.24em] text-white/50 mb-1">Moves</div>
          <div className="space-y-1">
            {moves.slice(0, compact ? 4 : 6).map((m) => {
              const liveCd = cooldowns ? (cooldowns[m.id] || 0) : 0;
              const onCd = liveCd > 0;
              return (
                <div key={m.id} className={`flex items-center gap-1.5 text-[11px] ${onCd ? "opacity-60" : ""}`}
                  data-testid={`move-row-${m.id}`}>
                  {/* V.034 — cd chip lives INLINE to the left of the ability name so
                      players don't need a separate cooldowns block below. Reserves
                      the same width whether ready or on-cd so rows don't jitter. */}
                  <span className={`shrink-0 w-8 text-center font-mono text-[9px] uppercase tracking-wider rounded px-1 py-0.5 border ${
                    onCd
                      ? "border-amber-400/40 bg-amber-400/10 text-amber-200"
                      : "border-emerald-400/30 bg-emerald-400/10 text-emerald-300/80"
                  }`} data-testid={`move-cd-${m.id}`}>
                    {onCd ? `cd${liveCd}` : "rdy"}
                  </span>
                  <ElementBadge element={m.element} />
                  <span className="text-white truncate flex-1">{m.name}</span>
                  <span className="font-mono text-[9px] text-white/50">×{m.mult ?? 1}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {resist.length > 0 && !compact && (
        <div className="mt-3">
          {/* Use the site-wide ResistGrid so Deep Dive matches the format */}
          <ResistGrid resist={resist} size="sm" />
        </div>
      )}
    </div>
  );
}

function StatCell({ label, value, icon: Icon, color, base = null, delta = 0 }) {
  // V.034 — Show the "started with" value or delta below the current value
  // so players see instantly how much a stat has shifted mid-dive.
  const showDelta = delta !== 0;
  const deltaCls = delta < 0 ? "text-rose-400" : "text-emerald-300";
  return (
    <div className="rounded-lg border border-white/10 bg-black/30 px-1.5 py-1" data-testid={`stat-cell-${label.toLowerCase()}`}>
      <div className={`inline-flex items-center gap-0.5 ${color}`}>
        <Icon className="h-3 w-3" />
        <span className="font-mono text-[9px] uppercase tracking-[0.18em]">{label}</span>
      </div>
      <div className="text-white font-semibold text-sm leading-tight">{value ?? "—"}</div>
      {showDelta && (
        <div className={`font-mono text-[9px] leading-tight ${deltaCls}`} data-testid={`stat-cell-${label.toLowerCase()}-delta`}>
          {delta > 0 ? `+${delta}` : delta}
        </div>
      )}
      {!showDelta && base != null && base !== value && (
        <div className="font-mono text-[9px] leading-tight text-white/35">{base}</div>
      )}
    </div>
  );
}

function ModChip({ label, pct }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/[0.05] px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.18em] text-white/70">
      {label} {Math.round(pct * 100)}%
    </span>
  );
}

function ElementBadge({ element, size = "sm" }) {
  const meta = ELEMENT_META[element] || { name: element, color: "#94a3b8", bg: "rgba(148,163,184,0.15)" };
  const px = size === "sm" ? "px-1.5 py-0.5" : "px-2 py-1";
  return (
    <span className={`inline-flex items-center rounded-full font-mono uppercase tracking-[0.18em] text-[9px] ${px}`}
      style={{ background: meta.bg, color: meta.color }} title={meta.name}>
      {meta.name}
    </span>
  );
}

function RunHistoryPanel({ items }) {
  if (!items?.length) return null;
  const OUTCOME_STYLES = {
    extracted: { icon: Trophy, cls: "text-emerald-300 border-emerald-400/40 bg-emerald-400/10", label: "Extracted" },
    failed:    { icon: Skull,  cls: "text-red-300 border-red-400/40 bg-red-400/10", label: "Felled" },
    abandoned: { icon: ArrowLeft, cls: "text-white/60 border-white/15 bg-white/[0.04]", label: "Abandoned" },
  };
  return (
    <div className="mt-10" data-testid="deep-dive-history-panel">
      <div className="flex items-center gap-2 mb-3">
        <History className="h-4 w-4 text-cyan-300" />
        <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-cyan-300">Recent Dives</span>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {items.map((it) => {
          const style = OUTCOME_STYLES[it.outcome] || OUTCOME_STYLES.abandoned;
          const Icon = style.icon;
          return (
            <div key={it.id} data-testid={`deep-dive-history-${it.id}`}
              className="rounded-xl border border-white/10 bg-black/40 p-3 flex gap-3">
              <div className="w-10 aspect-[3/4] rounded overflow-hidden bg-black/60 shrink-0">
                {it.card_snapshot?.front_image && (
                  <img src={it.card_snapshot.front_image} alt="" loading="lazy" className="w-full h-full object-cover" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.22em] ${style.cls}`}>
                    <Icon className="h-3 w-3" /> {style.label}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/40">
                    depth {it.depth_reached}
                  </span>
                </div>
                <div className="mt-1 text-white text-sm truncate">{it.card_snapshot?.title || "Unknown"}</div>
                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-white/55 font-mono">
                  <span><Coins className="inline h-3 w-3 mr-0.5" />{it.loot_credits}</span>
                  <span><Gem className="inline h-3 w-3 mr-0.5" />{it.loot_materials_count}</span>
                  <span><Sword className="inline h-3 w-3 mr-0.5" />{it.loot_gear_count}</span>
                  <span>· {it.enemies_defeated} kills · {it.chests_opened} chests</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RunView({ run, setRun, clearRun, onRunEnded }) {
  const qc = useQueryClient();
  const floor = run.floor;
  const player = run.player;
  const moveCooldowns = run.move_cooldowns || {};
  const [cellSize, setCellSize] = useState(() =>
    (typeof window !== "undefined" && window.innerWidth < 640) ? CELL_MIN : CELL);
  const mapScrollRef = useRef(null);
  const centeredRef = useRef(false);
  const [hoverEnemy, setHoverEnemy] = useState(null); // {enemy, x, y}
  const [attackFor, setAttackFor] = useState(null);   // enemy tile opened for attack
  const [npcMenu, setNpcMenu] = useState(null);       // {npc, actions} opened dialog
  const [bossCutIn, setBossCutIn] = useState(null);   // active boss title-card
  const [bossCutInShown, setBossCutInShown] = useState({}); // floors we've already cut-in'd
  const [abandonPicker, setAbandonPicker] = useState(false); // V.029 — abandon-selection modal

  // Show the boss cut-in the first time a boss becomes visible on the
  // current floor. Deduped per-floor via bossCutInShown so a re-render
  // (or optimistic move update) doesn't re-open the modal.
  useEffect(() => {
    const key = String(run.current_floor);
    if (bossCutInShown[key]) return;
    const boss = (floor.enemies || []).find((e) => e.is_boss && e.state === "alive");
    if (!boss) return;
    setBossCutIn({ boss, depth: run.current_floor });
    setBossCutInShown((prev) => ({ ...prev, [key]: true }));
  }, [run.current_floor, floor.enemies, bossCutInShown]);

  const move = useMutation({
    mutationFn: async ({ x, y }) => (await api.post("/deep-dive/move", { x, y })).data,
    // V.027 — OPTIMISTIC UPDATE: paint the player at the destination
    // immediately so keyboard/click feels instant. Reveal a 3-tile radius
    // around the destination locally too so fog-of-war doesn't visibly
    // pop after the server round-trip. If the server rejects (rare —
    // walkability was already checked client-side), the onError revert
    // returns the token home.
    onMutate: async ({ x, y }) => {
      const cachedRun = currentRun();
      const prev = qc.getQueryData(["deep-dive-current"]);
      const nx = x, ny = y;
      const cur = { ...cachedRun.floor };
      // Clone revealed row-by-row for the tiles we'll touch.
      const revealed = cur.revealed.map((r) => r.slice());
      for (let dy = -3; dy <= 3; dy++) {
        for (let dx = -3; dx <= 3; dx++) {
          const rx = nx + dx, ry = ny + dy;
          if (rx >= 0 && ry >= 0 && rx < cur.w && ry < cur.h) revealed[ry][rx] = true;
        }
      }
      cur.revealed = revealed;
      setRun({ ...cachedRun, player: { ...cachedRun.player, x: nx, y: ny }, floor: cur });
      return { prev };
    },
    onError: (e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(["deep-dive-current"], ctx.prev);
      toast.error(apiError(e, "Blocked"));
    },
    // Adopt the authoritative server run when it arrives.
    onSuccess: (serverRun) => setRun(serverRun),
    // Fire the buffered next move (if any) from the server-authoritative
    // position — see V.029 note above on the WASD serialisation fix.
    onSettled: () => {
      const buf = nextMoveRef.current;
      nextMoveRef.current = null;
      if (!buf) return;
      const p = currentPlayer();
      if (p) onTileClick(p.x + buf[0], p.y + buf[1]);
    },
  });
  const interact = useMutation({
    mutationFn: async ({ x, y, move_id }) => (await api.post("/deep-dive/interact", { x, y, move_id })).data,
    onSuccess: (d) => {
      setRun(d.run);
      setAttackFor(null);
      const r = d.result || {};
      if (r.kind === "npc_menu") {
        setNpcMenu({ npc: r.npc, actions: r.actions || [] });
        return;
      }
      if (r.kind === "chest_opened") {
        const parts = [`+${r.credits} credits`];
        if (r.materials?.length) parts.push(`${r.materials.reduce((s, m) => s + (m.qty || 0), 0)} mats`);
        if (r.gear) {
          parts.push(r.gear.critical_forge ? `${r.gear.rarity} ${r.gear.slot} · CRIT ×${r.gear.critical_forge.multiplier}` : `${r.gear.rarity} ${r.gear.slot}`);
        }
        toast.success(`Chest — ${parts.join(" · ")}`, { duration: 6000 });
      }
      if (r.kind === "door_opened") toast("Door opened — walk through");
      if (r.kind === "descend") toast(`Descended to depth ${r.depth}`);
      if (r.kind === "ascend") toast(`Ascended to depth ${r.depth}`);
      if (r.kind === "extract_prompt") toast("Runeplate reached — press Extract to leave.");
      if (r.kind === "encounter_won") {
        if (r.is_boss && r.boss_reward) {
          const parts = [];
          if (r.boss_reward.credits) parts.push(`+${r.boss_reward.credits} credits`);
          if (r.boss_reward.materials?.length) parts.push(`${r.boss_reward.materials.reduce((s, m) => s + (m.qty || 1), 0)} T${Math.max(...r.boss_reward.materials.map((m) => m.tier))} mats`);
          if (r.boss_reward.gear) parts.push(`${r.boss_reward.gear.rarity} ${r.boss_reward.gear.slot}${r.boss_reward.gear.critical_forge ? ` · CRIT ×${r.boss_reward.gear.critical_forge.multiplier}` : ""}`);
          toast.success(`⚔ ${r.enemy} felled — ${parts.join(" · ")}`, { duration: 9000 });
        } else {
          const detail = (r.turn_log || []).slice(0, 2).join(" · ");
          toast.success(`Defeated ${r.enemy}${detail ? " · " + detail : ""}`);
        }
      }
      if (r.kind === "encounter_partial") {
        const detail = (r.turn_log || []).join(" · ");
        toast(`${detail || r.enemy}`, { duration: 5000 });
      }
      if (r.kind === "encounter_lost") {
        const del = r.delivered || {};
        const parts = [];
        if (del.credits) parts.push(`+${del.credits} credits`);
        if (del.materials?.length) parts.push(`${del.materials.length} mats returned`);
        if (del.gear_items?.length) parts.push(`${del.gear_items.length} gear kept`);
        toast.error(`${r.enemy} felled you at depth ${r.summary?.depth_reached ?? 0}. Card safe. ${parts.length ? "Recovered: " + parts.join(" · ") : ""}`, { duration: 9000 });
        clearRun();
        onRunEnded({
          outcome: "failed",
          headline: `${r.enemy} felled you`,
          sub: `Depth ${r.summary?.depth_reached ?? 0} · your card is safe`,
          delivered: del,
          depth_reached: r.summary?.depth_reached ?? 0,
          steps: r.summary?.steps ?? 0,
          card: run.card_snapshot,
        });
      }
    },
    onError: (e) => { setAttackFor(null); toast.error(apiError(e, "Nothing happens")); },
  });
  const extract = useMutation({
    mutationFn: async () => (await api.post("/deep-dive/extract")).data,
    onSuccess: (d) => {
      const s = d.summary || {};
      const del = s.delivered || {};
      const cardSnap = run.card_snapshot;
      clearRun();
      onRunEnded({
        outcome: "extracted",
        headline: "Extraction complete",
        sub: `Depth ${s.depth_reached} · ${s.steps} steps`,
        delivered: del,
        depth_reached: s.depth_reached,
        steps: s.steps,
        card: cardSnap,
      });
    },
    onError: (e) => toast.error(apiError(e, "Cannot extract here")),
  });
  const abandon = useMutation({
    mutationFn: async ({ keep }) => (await api.post("/deep-dive/abandon", { keep })).data,
    onSuccess: (d) => {
      const del = d.delivered || {};
      const cardSnap = run.card_snapshot;
      setAbandonPicker(false);
      clearRun();
      onRunEnded({
        outcome: "abandoned",
        headline: "Run abandoned",
        sub: `Your card is safe · kept ${(del.materials?.length || 0) + (del.gear_items?.length || 0) + (del.credits ? 1 : 0)} / ${d.keep_cap} allowed`,
        delivered: del,
        card: cardSnap,
      });
    },
    onError: (e) => toast.error(apiError(e, "Failed")),
  });

  const npcInteract = useMutation({
    mutationFn: async ({ x, y, action_id, params }) => (await api.post("/deep-dive/npc-interact", { x, y, action_id, params: params || {} })).data,
    onSuccess: (d) => {
      setRun(d.run);
      // V.034 — mirror the fresh run into the query cache so every accessor
      // that reads `deep-dive-current` (DelveBag, RunSummary, movement
      // walkable check) sees the new bank / gear tiers immediately.
      qc.setQueryData(["deep-dive-current"], (prev) => ({ ...(prev || {}), active: true, run: d.run }));
      // Refresh menu — if NPC was exhausted, close it. Otherwise swap in
      // the fresh actions list so upgraded gear rows re-render at the new
      // tier (Blacksmith) or spent materials drop off (Trader).
      if (d.npc?.state === "exhausted") {
        setNpcMenu(null);
      } else {
        setNpcMenu((m) => m ? { ...m, npc: d.npc, actions: d.actions || m.actions } : null);
      }
      const o = d.outcome || {};
      if (o.traded) toast.success(`Traded 3× T${o.traded.consumed.tier} → 1× T${o.traded.gained.tier}`);
      if (o.purchased) toast.success(`Bought ${o.purchased.rarity} ${o.purchased.slot} for ${o.purchased.price} credits`);
      if (o.healed !== undefined) toast.success(`Healed ${o.healed} HP for ${o.cost} credits`);
      if (o.revealed) toast.success(`The floor is fully revealed (−${o.cost} credits)`);
      if (o.descent_unlocked) toast.success(`Descent seal broken (−${o.cost} credits) — go before enemies close in!`);
      if (o.reforged) toast.success(`Reforged to tier ${o.reforged.new_tier} for ${o.reforged.cost} credits`);
      if (o.duel === "win") toast.success(`Duel won! +${o.reward_credits} credits, +1 material`);
      if (o.duel === "loss") toast(`Duel lost — you lose ${o.hp_lost} HP but stay standing.`);
      if (o.quest_accepted) toast.success(`Quest accepted: ${o.quest_accepted.title}`);
      if (o.quest_reward) toast.success(`Quest completed — +${o.quest_reward} credits banked`);
      if (o.dismissed) toast(`${d.npc?.name || "They"} step aside. The tile clears.`);
    },
    onError: (e) => toast.error(apiError(e, "The NPC won't have it")),
  });

  // Fresh accessors — closures over `player`/`floor` go stale between
  // renders (particularly during the WASD serialisation buffer's onSettled
  // callback that runs before React commits the setRun update). Reading
  // the query cache is always authoritative.
  const currentRun = () => qc.getQueryData(["deep-dive-current"])?.run || run;
  const currentPlayer = () => currentRun().player || player;
  const currentFloor = () => currentRun().floor || floor;

  const doorAt = (x, y) => (currentFloor().doors || []).find((d) => d.x === x && d.y === y);
  const enemyAt = (x, y) => (currentFloor().enemies || []).find((e) => e.x === x && e.y === y && e.state === "alive");
  const npcAt = (x, y) => (currentFloor().npcs || []).find((n) => n.x === x && n.y === y);

  const onTileClick = (x, y) => {
    const p = currentPlayer();
    const f = currentFloor();
    const dx = Math.abs(x - p.x);
    const dy = Math.abs(y - p.y);
    const adj = dx + dy === 1;
    const same = dx === 0 && dy === 0;
    const t = f.tiles[y][x];
    if (same && (t === TILE.STAIRS_DOWN || t === TILE.STAIRS_UP || t === TILE.EXTRACT)) {
      interact.mutate({ x, y });
    } else if (adj && t === TILE.ENEMY) {
      // Open attack picker instead of auto-swinging.
      setAttackFor({ x, y, enemy: enemyAt(x, y) });
    } else if (adj && t === TILE.DOOR) {
      const d = doorAt(x, y);
      if (d && d.state === "open") {
        move.mutate({ x, y });  // walk through
      } else {
        interact.mutate({ x, y });  // open it
      }
    } else if (adj && t === TILE.CHEST) {
      interact.mutate({ x, y });
    } else if (adj && t === TILE.NPC) {
      // Exhausted NPCs (fully dealt with) become walkable — treat the tile as
      // an opened door so cleared prep rooms don't wall off the boss chamber.
      const n = npcAt(x, y);
      if (n && n.state === "exhausted") {
        move.mutate({ x, y });
      } else {
        interact.mutate({ x, y });
      }
    } else if (adj && t === TILE.BOSS_DOOR) {
      // Boss doors are always walkable — one-click passage into the boss room.
      move.mutate({ x, y });
    } else if (adj && (t === TILE.FLOOR || t === TILE.STAIRS_DOWN || t === TILE.STAIRS_UP || t === TILE.EXTRACT)) {
      move.mutate({ x, y });
    }
  };

  // WASD / arrow keys movement. Reuses onTileClick so keyboard mirrors the
  // click behaviour exactly (walk, open door, engage enemy, talk to NPC).
  // V.029 — moves are now SERIALISED to prevent the race where two
  // concurrent /move POSTs landed out of order and the server rejected
  // the loser as non-adjacent. Hammer-key experience is preserved via a
  // 1-deep "next move" buffer that fires after the current one settles.
  const nextMoveRef = useRef(null);
  useWindowKeydown((e) => {
      if (attackFor || npcMenu) return;
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      if (e.altKey || e.metaKey || e.ctrlKey) return;
      const dir = ({
        w: [0, -1], W: [0, -1], ArrowUp: [0, -1],
        a: [-1, 0], A: [-1, 0], ArrowLeft: [-1, 0],
        s: [0, 1],  S: [0, 1],  ArrowDown: [0, 1],
        d: [1, 0],  D: [1, 0],  ArrowRight: [1, 0],
      })[e.key];
      if (!dir) return;
      e.preventDefault();
      if (interact.isPending) return;
      if (move.isPending) {
        // Buffer only the LATEST direction so a burst of presses coalesces
        // into one follow-up move (fired from the server-authoritative
        // position when the in-flight request settles).
        nextMoveRef.current = dir;
        return;
      }
      const p = currentPlayer();
      onTileClick(p.x + dir[0], p.y + dir[1]);
  });

  const width = floor.w * cellSize;
  const height = floor.h * cellSize;

  // Center the map viewport on the player once it first renders, so a map
  // larger than the (mobile) screen opens focused on the token instead of
  // the top-left corner.
  useEffect(() => {
    if (centeredRef.current || !mapScrollRef.current) return;
    const el = mapScrollRef.current;
    if (!el.clientWidth) return;
    el.scrollLeft = Math.max(0, player.x * cellSize - el.clientWidth / 2);
    el.scrollTop = Math.max(0, player.y * cellSize - el.clientHeight / 2);
    centeredRef.current = true;
  }, [player.x, player.y, cellSize]);

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-6 pt-6 pb-28 lg:pb-6" data-testid="deep-dive-run">
      {/* Two-column layout: card panel on left, dungeon grid on right */}
      <div className="grid gap-4 lg:grid-cols-[18rem_1fr]">
        {/* Player card panel — persistent stats OUTSIDE the delve box */}
        <aside className="min-w-0 rounded-2xl border border-white/10 bg-black/40 p-3" data-testid="deep-dive-player-panel">
          {/* Mobile: compact row (small art + name beside it) so the map
              isn't pushed off-screen. lg: full-width card art stacked. */}
          <div className="flex gap-3 lg:block mb-2">
            <div className="w-24 shrink-0 lg:w-full aspect-[3/4] rounded-lg overflow-hidden bg-black/60 lg:mb-2">
              {run.card_snapshot?.front_image && (
                <img src={run.card_snapshot.front_image} alt={run.card_snapshot.title} className="w-full h-full object-cover" />
              )}
            </div>
            <div className="min-w-0 self-center lg:self-auto">
              <div className="text-white text-sm font-semibold truncate">{run.card_snapshot?.title}</div>
              <div className="font-mono text-[9px] uppercase tracking-[0.24em] text-white/40">{run.card_snapshot?.rarity}</div>
            </div>
          </div>
          {/* V.034 — Stats now show live current values with a delta from the
              value the card entered the delve with (HP is the main mover;
              ATK/DEF/SPD stay flat unless gear/reforge shifts them). */}
          <CardStatsPanel
            card={{ arena: run.card_snapshot?.arena }}
            current={{ hp: run.player.hp, atk: run.player.atk, def: run.player.def, spd: run.player.spd }}
            cooldowns={moveCooldowns}
          />
          {/* V.034 — Run status chips moved out of the top bar into the
              player aside so all "who am I & how am I doing" info lives in
              one place. */}
          <div className="mt-3 flex flex-wrap gap-1.5" data-testid="deep-dive-run-chips">
            <span className="inline-flex items-center gap-1 rounded-full border border-cyan-300/40 bg-cyan-400/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.24em] text-cyan-200">
              <Compass className="h-3 w-3" /> Depth {run.current_floor}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full border border-red-400/40 bg-red-400/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.24em] text-red-200" data-testid="deep-dive-hp">
              <Heart className="h-3 w-3" /> {run.player.hp} / {run.player.hp_max}
            </span>
            {run.loot_banked && (run.loot_banked.credits > 0 || run.loot_banked.materials?.length > 0 || run.loot_banked.gear_items?.length > 0) && (
              <span className="inline-flex items-center gap-2 rounded-full border border-amber-400/40 bg-amber-400/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.24em] text-amber-200"
                data-testid="deep-dive-loot-chip"
                title="Banked loot — delivered when you extract, or partially on loss">
                <span className="inline-flex items-center gap-0.5"><Coins className="h-3 w-3" /> {run.loot_banked.credits}</span>
                <span className="inline-flex items-center gap-0.5"><Gem className="h-3 w-3" /> {(run.loot_banked.materials || []).reduce((s, m) => s + (m.qty || 0), 0)}</span>
                <span className="inline-flex items-center gap-0.5"><Sword className="h-3 w-3" /> {(run.loot_banked.gear_items || []).length}</span>
              </span>
            )}
            <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-white/40 w-full mt-0.5">
              seed {run.seed}
            </span>
          </div>
          {/* Extract + Abandon live with the card panel too. */}
          <div className="mt-3 grid grid-cols-2 gap-1.5">
            <Button size="sm" variant="outline" onClick={() => extract.mutate()} disabled={extract.isPending}
              data-testid="deep-dive-extract"
              className="border-emerald-400/40 text-emerald-200 hover:bg-emerald-400/10">
              <Home className="h-3.5 w-3.5 mr-1" /> Extract
            </Button>
            <Button size="sm" variant="outline" onClick={() => setAbandonPicker(true)}
              disabled={abandon.isPending}
              data-testid="deep-dive-abandon"
              className="border-white/15 text-white/70 hover:bg-white/5">
              <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Abandon
            </Button>
          </div>
        </aside>

        {/* Grid + zoom controls */}
        <div className="min-w-0">
          {/* V.033 — Delve Bag sits above the map so loot is highly visible
              during a run, instead of buried below the player card. */}
          <DelveBag bank={run.loot_banked} />
          <div className="flex items-center gap-1.5 mb-2 mt-3">
            <Button size="sm" variant="outline" onClick={() => setCellSize((s) => Math.max(CELL_MIN, s - 2))}
              data-testid="deep-dive-zoom-out"
              className="border-white/15 h-7 w-7 p-0"><ZoomOut className="h-3.5 w-3.5" /></Button>
            <Button size="sm" variant="outline" onClick={() => setCellSize((s) => Math.min(CELL_MAX, s + 2))}
              data-testid="deep-dive-zoom-in"
              className="border-white/15 h-7 w-7 p-0"><ZoomIn className="h-3.5 w-3.5" /></Button>
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/40 ml-1">Zoom {Math.round((cellSize / CELL) * 100)}%</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/40 ml-auto">{run.history_length} events</span>
          </div>

          <div className="w-full rounded-2xl border border-white/10 bg-black/50 p-3 overflow-auto max-h-[70vh] relative"
            ref={mapScrollRef}
            data-testid="deep-dive-map"
            onWheel={(e) => {
              // V.026 — plain scroll wheel zooms (no ctrl needed) since the
              // map is the only scrollable region here anyway.
              e.preventDefault();
              setCellSize((s) => Math.min(CELL_MAX, Math.max(CELL_MIN, s + (e.deltaY < 0 ? 2 : -2))));
            }}>
            <svg width={width} height={height} className="block mx-auto">
              {floor.tiles.flatMap((row, y) => row.map((tile, x) => ({ tile, x, y, id: `${x}:${y}` }))).map(({ tile: t, x, y, id: tileId }) => {
                // V.033 — an NPC tile with an exhausted occupant renders as a
                // plain floor so the map matches its new walkable state.
                let effective = t;
                if (t === TILE.NPC) {
                  const n = (floor.npcs || []).find((nn) => nn.x === x && nn.y === y);
                  if (n && n.state === "exhausted") effective = TILE.FLOOR;
                }
                return (
                  <rect
                    key={tileId}
                    x={x * cellSize} y={y * cellSize} width={cellSize - 1} height={cellSize - 1}
                    fill={floor.revealed[y][x] ? TILE_COLORS[effective] : "#050709"}
                    stroke={floor.revealed[y][x] && effective !== TILE.WALL ? "#00000040" : "transparent"}
                    onClick={() => floor.revealed[y][x] && onTileClick(x, y)}
                    style={{ cursor: floor.revealed[y][x] && effective !== TILE.WALL ? "pointer" : "default" }}
                    data-testid={`deep-dive-tile-${x}-${y}`}
                  />
                );
              })}
              {/* Doors — draw a distinct notch */}
              {(floor.doors || []).map((d) => (
                <rect key={`d-${d.x}-${d.y}`} x={d.x * cellSize + 4} y={d.y * cellSize + 4} width={cellSize - 9} height={cellSize - 9}
                  fill={d.state === "open" ? "#3fb85e" : "#7c5c2b"} pointerEvents="none" />
              ))}
              {/* Chests */}
              {(floor.chests || []).filter((c) => c.state === "closed").map((c) => (
                <rect key={`c-${c.x}-${c.y}`} x={c.x * cellSize + 5} y={c.y * cellSize + 5} width={cellSize - 11} height={cellSize - 11}
                  fill="#facc15" stroke="#78350f" strokeWidth={1.5} pointerEvents="none" />
              ))}
              {/* Enemies with hover tooltip trigger. Bosses render bigger
                  with a pulsing aura so they read at-a-glance. */}
              {(floor.enemies || []).filter((e) => e.state === "alive").map((e) => (
                <g key={`e-${e.x}-${e.y}`}
                  onClick={() => onTileClick(e.x, e.y)}
                  onMouseEnter={() => setHoverEnemy({ enemy: e, screenX: e.x * cellSize + cellSize, screenY: e.y * cellSize })}
                  onMouseLeave={() => setHoverEnemy(null)}
                  style={{ cursor: "pointer" }}
                  data-testid={`deep-dive-enemy-${e.x}-${e.y}${e.is_boss ? "-boss" : ""}`}>
                  {e.is_boss ? (
                    <>
                      {/* Pulsing outer aura */}
                      <rect x={e.x * cellSize - cellSize * 0.15} y={e.y * cellSize - cellSize * 0.15}
                        width={cellSize * 1.3} height={cellSize * 1.3}
                        fill="rgba(250,204,21,0.18)" stroke="#fde047" strokeWidth={2} rx={4} pointerEvents="none">
                        <animate attributeName="opacity" values="0.5;1;0.5" dur="1.6s" repeatCount="indefinite" />
                      </rect>
                      <rect x={e.x * cellSize + 1} y={e.y * cellSize + 1} width={cellSize - 3} height={cellSize - 3}
                        fill="rgba(239,68,68,0.35)" stroke="#fde047" strokeWidth={3} rx={3} pointerEvents="none" />
                      <circle cx={e.x * cellSize + cellSize / 2} cy={e.y * cellSize + cellSize / 2}
                        r={Math.max(4, cellSize / 3.5)} fill="#fde047" pointerEvents="none">
                        <animate attributeName="r" values={`${Math.max(4, cellSize / 3.5)};${Math.max(5, cellSize / 3)};${Math.max(4, cellSize / 3.5)}`} dur="1.6s" repeatCount="indefinite" />
                      </circle>
                    </>
                  ) : (
                    <>
                      <rect x={e.x * cellSize + 1} y={e.y * cellSize + 1} width={cellSize - 3} height={cellSize - 3}
                        fill="rgba(239,68,68,0.15)" stroke="#ef4444" strokeWidth={2.5} rx={2} pointerEvents="none" />
                      <circle cx={e.x * cellSize + cellSize / 2} cy={e.y * cellSize + cellSize / 2}
                        r={Math.max(3, cellSize / 5)} fill="#ef4444" pointerEvents="none" />
                    </>
                  )}
                  {/* HP bar under enemy */}
                  <rect x={e.x * cellSize + 2} y={e.y * cellSize + cellSize - 4} width={cellSize - 5} height={2} fill="#3f0a0a" pointerEvents="none" />
                  <rect x={e.x * cellSize + 2} y={e.y * cellSize + cellSize - 4}
                    width={Math.max(0, (cellSize - 5) * (e.hp_current / e.hp))} height={2} fill={e.is_boss ? "#fde047" : "#ef4444"} pointerEvents="none" />
                </g>
              ))}
              {/* NPCs — friendly tokens */}
              {(floor.npcs || []).filter((n) => n.state === "available").map((n) => {
                const meta = NPC_ROLE_META[n.key] || { color: "#a3a3a3" };
                return (
                  <g key={`n-${n.x}-${n.y}`}
                    onClick={() => onTileClick(n.x, n.y)}
                    style={{ cursor: "pointer" }}
                    data-testid={`deep-dive-npc-${n.key}`}>
                    <rect x={n.x * cellSize + 1} y={n.y * cellSize + 1} width={cellSize - 3} height={cellSize - 3}
                      fill="rgba(167,139,250,0.12)" stroke={meta.color} strokeWidth={2.5} rx={3} pointerEvents="none" />
                    <circle cx={n.x * cellSize + cellSize / 2} cy={n.y * cellSize + cellSize / 2}
                      r={Math.max(3, cellSize / 4.5)} fill={meta.color} pointerEvents="none" />
                  </g>
                );
              })}
              {/* Player card token */}
              <g transform={`translate(${player.x * cellSize} ${player.y * cellSize})`} pointerEvents="none">
                <rect x={1} y={1} width={cellSize - 3} height={cellSize - 3} fill="url(#playerFill)" stroke="#22d3ee" strokeWidth={2} rx={3} />
              </g>
              <defs>
                <pattern id="playerFill" width={cellSize} height={cellSize} patternUnits="userSpaceOnUse">
                  {run.card_snapshot?.front_image
                    ? <image href={run.card_snapshot.front_image} x={0} y={0} width={cellSize} height={cellSize} preserveAspectRatio="xMidYMid slice" />
                    : <rect width={cellSize} height={cellSize} fill="#22d3ee" />}
                </pattern>
              </defs>
            </svg>

            {hoverEnemy && (
              <EnemyTooltip enemy={hoverEnemy.enemy}
                style={{ left: hoverEnemy.screenX + 20, top: hoverEnemy.screenY + 12, position: "absolute", pointerEvents: "none" }} />
            )}
          </div>

          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] font-mono uppercase tracking-[0.22em] text-white/50">
            <LegendChip color={TILE_COLORS[1]} label="Floor" />
            <LegendChip color={TILE_COLORS[2]} label="Door" icon={DoorOpen} />
            <LegendChip color={TILE_COLORS[3]} label="Stairs down" icon={ChevronDown} />
            <LegendChip color={TILE_COLORS[4]} label="Stairs up" icon={ChevronUp} />
            <LegendChip color="#facc15" label="Chest" icon={Package} />
            <LegendChip color={TILE_COLORS[6]} label="Extract" icon={Sparkles} />
            <LegendChip color="#ef4444" label="Enemy" />
            <LegendChip color="#a78bfa" label="NPC" icon={UserPlus} />
            <LegendChip color="#facc15" label="Boss door" icon={KeyRound} />
          </div>
          <p className="mt-3 text-[11px] text-white/40">Click a tile or use <strong className="text-white/70">WASD / arrow keys</strong> to move · click enemies to pick a move · click NPCs to trade/heal/etc · hover enemies for resistances · scroll wheel to zoom · <strong className="text-red-300">stairs down are sealed until every enemy on this floor is defeated</strong> (or find a Gatekeeper NPC to bypass).</p>
        </div>
      </div>

      {attackFor && (
        <AttackPicker
          enemy={attackFor.enemy}
          arena={run.card_snapshot?.arena}
          cooldowns={moveCooldowns}
          pending={interact.isPending}
          onCancel={() => setAttackFor(null)}
          onPick={(mv) => interact.mutate({ x: attackFor.x, y: attackFor.y, move_id: mv.id })}
        />
      )}

      {npcMenu && (
        <NPCMenu
          npc={npcMenu.npc}
          actions={npcMenu.actions}
          pending={npcInteract.isPending}
          onCancel={() => setNpcMenu(null)}
          onPick={(action) => npcInteract.mutate({ x: npcMenu.npc.x, y: npcMenu.npc.y, action_id: action.id, params: action.meta })}
        />
      )}

      {bossCutIn && (
        <BossCutIn boss={bossCutIn.boss} depth={bossCutIn.depth} onClose={() => setBossCutIn(null)} />
      )}

      {abandonPicker && (
        <AbandonPicker
          bank={run.loot_banked}
          cap={run.current_floor}
          pending={abandon.isPending}
          onCancel={() => setAbandonPicker(false)}
          onConfirm={(keep) => abandon.mutate({ keep })}
        />
      )}
    </div>
  );
}

function DelveBag({ bank }) {
  const materials = bank?.materials || [];
  const gear = bank?.gear_items || [];
  if (materials.length === 0 && gear.length === 0 && !(bank?.credits > 0)) return null;
  // Aggregate materials by "mat_name @ tier" so 2 chest drops of the same
  // mat stack in the display without hiding what's in the bag.
  const matMap = new Map();
  for (const m of materials) {
    const label = m.mat_name || `Tier ${m.tier} Material`;
    const key = `${label}::${m.tier}`;
    const prev = matMap.get(key) || { label, tier: m.tier, qty: 0, mat_key: m.mat_key };
    prev.qty += m.qty || 1;
    matMap.set(key, prev);
  }
  const aggregated = Array.from(matMap.values()).sort((a, b) => b.tier - a.tier || a.label.localeCompare(b.label));
  return (
    <div className="mb-3" data-testid="deep-dive-delve-bag">
      <div className="font-mono text-[9px] uppercase tracking-[0.24em] text-white/50 mb-1">Delve Bag</div>
      <div className="rounded-lg border border-amber-400/25 bg-amber-400/5 p-2 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-1">
        {bank.credits > 0 && (
          <div className="flex items-center gap-1.5 text-[11px]">
            <Coins className="h-3 w-3 text-amber-300 shrink-0" />
            <span className="text-white flex-1 truncate">Credits</span>
            <span className="font-mono text-amber-200">{bank.credits}</span>
          </div>
        )}
        {aggregated.map((m) => {
          const MIcon = materialIcon(m.mat_key);
          return (
            <div key={`${m.label}-${m.tier}`} className="flex items-center gap-1.5 text-[11px]" data-testid={`delve-bag-mat-${m.label.toLowerCase().replace(/\s+/g, '-')}-t${m.tier}`}>
              <MIcon className="h-3 w-3 text-emerald-300 shrink-0" />
              <span className="text-white flex-1 truncate">
                <span className="font-mono text-[9px] uppercase text-emerald-300/80 mr-1">T{m.tier}</span>
                {m.label}
              </span>
              <span className="font-mono text-emerald-200">×{m.qty}</span>
            </div>
          );
        })}
        {gear.map((g) => (
          <div key={g.id} className="group relative flex items-center gap-1.5 text-[11px]" data-testid={`delve-bag-gear-${g.id}`}>
            <InventoryIcon item={g} className="h-3 w-3" />
            <span className="text-white flex-1 truncate">
              <span className="font-mono text-[9px] uppercase text-orange-300/80 mr-1">{g.rarity}</span>
              <InventoryName item={g} className="text-[11px]" />
              {g.critical_forge && <span className="ml-1 text-amber-300 font-mono text-[9px]">CRIT ×{g.critical_forge.multiplier}</span>}
            </span>
            <span className="font-mono text-orange-200">{g.slot}</span>
            <GearHoverCard it={g} slotLabel={g.slot} />
          </div>
        ))}
      </div>
    </div>
  );
}

function AbandonPicker({ bank, cap, pending, onCancel, onConfirm }) {
  const [keep, setKeep] = useState(new Set());
  const items = useMemo(() => {
    const out = [];
    if ((bank?.credits || 0) > 0) {
      out.push({ id: "credits", label: `${bank.credits} credits`, icon: Coins, iconCls: "text-amber-300" });
    }
    (bank?.materials || []).forEach((m, i) => {
      out.push({
        id: `material:${i}`,
        label: `${m.mat_name || `Tier ${m.tier} Material`} ×${m.qty}`,
        sub: `T${m.tier}`,
        icon: materialIcon(m.mat_key), iconCls: "text-emerald-300",
      });
    });
    (bank?.gear_items || []).forEach((g) => {
      out.push({
        id: `gear:${g.id}`,
        label: `${g.name}${g.critical_forge ? ` · CRIT ×${g.critical_forge.multiplier}` : ""}`,
        sub: `${g.rarity} · ${g.slot}`,
        icon: inventoryVisual(g).icon, iconCls: "",
        gear: g,  // full gear payload so the row can render a hover tooltip
      });
    });
    return out;
  }, [bank]);

  const toggle = (id) => {
    setKeep((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < cap) next.add(id);
      else return prev; // at cap
      return next;
    });
  };

  const noneAllowed = cap === 0;
  return (
    <div className="fixed inset-0 z-40 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onCancel} data-testid="deep-dive-abandon-picker">
      <div className="rounded-2xl border border-red-400/40 bg-neutral-950 p-4 max-w-lg w-full shadow-2xl"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-red-300">Abandon Dive</div>
            <div className="text-white text-lg font-semibold">Choose what to keep</div>
            <div className="text-white/60 text-xs mt-0.5">
              {noneAllowed
                ? "You're on the surface — you leave with nothing. Descend at least one floor to salvage loot on abandon."
                : <>Depth cap allows up to <strong className="text-red-200">{cap}</strong> item{cap === 1 ? "" : "s"}. Anything not selected is destroyed.</>}
            </div>
          </div>
          <button onClick={onCancel} className="text-white/50 hover:text-white" data-testid="deep-dive-abandon-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {items.length === 0 ? (
          <div className="rounded-lg border border-white/10 bg-black/40 p-4 text-center text-white/40 text-xs">
            Nothing banked. Abandoning walks away clean.
          </div>
        ) : (
          <div className="grid gap-1.5 max-h-[45vh] overflow-y-auto">
            {items.map((it) => {
              const checked = keep.has(it.id);
              const disabled = !checked && keep.size >= cap;
              const Icon = it.icon;
              return (
                <button key={it.id}
                  onClick={() => !noneAllowed && toggle(it.id)}
                  disabled={noneAllowed || (disabled && !checked)}
                  data-testid={`deep-dive-abandon-item-${it.id}`}
                  className={`group relative w-full text-left rounded-lg border p-2.5 transition-colors flex items-center gap-2 ${
                    checked
                      ? "border-red-300/60 bg-red-400/[0.08]"
                      : disabled || noneAllowed
                        ? "border-white/5 bg-white/[0.02] opacity-40 cursor-not-allowed"
                        : "border-white/15 bg-black/40 hover:bg-white/[0.06]"
                  }`}>
                  <span className={`inline-flex items-center justify-center h-4 w-4 rounded border ${checked ? "border-red-300 bg-red-400/40" : "border-white/25"}`}>
                    {checked && <span className="text-red-100 text-[9px] leading-none">✓</span>}
                  </span>
                  <Icon className={`h-3.5 w-3.5 ${it.iconCls}`} />
                  <span className="text-white text-sm flex-1 truncate">
                    {it.sub && <span className="font-mono text-[9px] uppercase text-white/50 mr-1">{it.sub}</span>}
                    {it.label}
                  </span>
                  {it.gear && <GearHoverCard it={it.gear} slotLabel={it.gear.slot} />}
                </button>
              );
            })}
          </div>
        )}

        <div className="mt-4 flex items-center gap-2">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/50 flex-1"
            data-testid="deep-dive-abandon-count">
            {noneAllowed ? "" : `${keep.size} / ${cap} selected`}
          </div>
          <Button variant="outline" size="sm" onClick={onCancel}
            className="border-white/15 text-white/70">Cancel</Button>
          <Button size="sm" onClick={() => onConfirm(Array.from(keep))} disabled={pending}
            data-testid="deep-dive-abandon-confirm"
            className="bg-red-500 text-white hover:bg-red-400">
            {pending ? "Abandoning…" : (keep.size === 0 ? "Abandon (lose all)" : `Abandon · keep ${keep.size}`)}
          </Button>
        </div>
      </div>
    </div>
  );
}


function RunSummaryModal({ summary, card, onClose }) {
  const del = summary.delivered || {};
  const materials = del.materials || [];
  const gear = del.gear_items || [];
  const HERO = {
    extracted: { icon: Trophy, cls: "border-emerald-400/50 text-emerald-200", accent: "#34d399" },
    failed:    { icon: Skull,  cls: "border-red-500/50 text-red-200",         accent: "#ef4444" },
    abandoned: { icon: ArrowLeft, cls: "border-white/25 text-white/70",       accent: "#a3a3a3" },
  }[summary.outcome] || { icon: Trophy, cls: "border-white/25 text-white", accent: "#ffffff" };
  const Icon = HERO.icon;
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose} data-testid="deep-dive-summary-modal">
      <div className={`rounded-2xl border p-5 max-w-lg w-full shadow-2xl bg-neutral-950 ${HERO.cls}`}
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3 mb-4">
          {card?.front_image && (
            <div className="w-20 aspect-[3/4] rounded-lg overflow-hidden bg-black/60 shrink-0">
              <img src={card.front_image} alt={card.title} className="w-full h-full object-cover" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <Icon className="h-4 w-4" style={{ color: HERO.accent }} />
              <span className="font-mono text-[10px] uppercase tracking-[0.32em]" style={{ color: HERO.accent }}>
                {summary.outcome}
              </span>
            </div>
            <div className="text-white text-xl font-semibold mt-0.5">{summary.headline}</div>
            <div className="text-white/50 text-xs mt-0.5">{summary.sub}</div>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="deep-dive-summary-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-2 mb-4">
          <StatCell label="Credits" value={del.credits ?? 0} icon={Coins} color="text-amber-300" />
          <StatCell label="Materials" value={materials.reduce((s, m) => s + (m.qty || 1), 0)} icon={Gem} color="text-emerald-300" />
          <StatCell label="Gear" value={gear.length} icon={Sword} color="text-orange-300" />
        </div>

        <div className="rounded-xl border border-white/10 bg-black/40 p-3 max-h-[45vh] overflow-y-auto space-y-2">
          {materials.length === 0 && gear.length === 0 && del.credits === 0 && (
            <div className="text-white/40 text-xs italic text-center py-2">No loot banked from this dive.</div>
          )}
          {del.credits > 0 && (
            <div className="flex items-center gap-2 text-sm">
              <Coins className="h-3.5 w-3.5 text-amber-300" />
              <span className="text-white flex-1">Banked credits</span>
              <span className="font-mono text-amber-300">+{del.credits}</span>
            </div>
          )}
          {materials.length > 0 && (
            <div>
              <div className="font-mono text-[9px] uppercase tracking-[0.24em] text-emerald-300/80 mt-2 mb-1">Materials</div>
              {keyedEntries(materials, (m) => `${m.mat_key}:${m.tier}`).map(({ item: m, key }, i) => {
                const MIcon = materialIcon(m.mat_key);
                return (
                  <div key={key} className="flex items-center gap-2 text-sm py-0.5" data-testid={`summary-mat-${i}`}>
                    <MIcon className="h-3.5 w-3.5 text-emerald-300" />
                    <span className="text-white flex-1 truncate">
                      <span className="font-mono text-[9px] uppercase text-emerald-300/70 mr-1.5">T{m.tier}</span>
                      {m.mat_name || `Tier ${m.tier} Material`}
                    </span>
                    <span className="font-mono text-emerald-200">×{m.qty}</span>
                  </div>
                );
              })}
            </div>
          )}
          {gear.length > 0 && (
            <div>
              <div className="font-mono text-[9px] uppercase tracking-[0.24em] text-orange-300/80 mt-2 mb-1">Gear</div>
              {gear.map((g) => (
                <div key={g.id} className="group relative flex items-center gap-2 text-sm py-0.5" data-testid={`summary-gear-${g.id}`}>
                  <InventoryIcon item={g} className="h-3.5 w-3.5" />
                  <span className="text-white flex-1 truncate">
                    <span className="font-mono text-[9px] uppercase text-orange-300/70 mr-1.5">{g.rarity}</span>
                    <InventoryName item={g} className="text-xs" />
                    {g.critical_forge && <span className="ml-1 text-amber-300 font-mono text-[9px]">CRIT ×{g.critical_forge.multiplier}</span>}
                  </span>
                  <span className="font-mono text-orange-200">{g.slot}</span>
                  <GearHoverCard it={g} slotLabel={g.slot} />
                </div>
              ))}
            </div>
          )}
        </div>

        <Button onClick={onClose} data-testid="deep-dive-summary-continue"
          className="w-full mt-4 bg-white text-black hover:bg-white/90">
          Continue
        </Button>
      </div>
    </div>
  );
}

function EnemyTooltip({ enemy, style }) {
  const resistArray = Object.entries(enemy.resist || {}).map(([element, pct]) => ({ element, pct }));
  return (
    <div className="z-50 rounded-xl border border-red-400/40 bg-black/95 backdrop-blur px-3 py-2 shadow-lg min-w-[16rem] max-w-[22rem]"
      style={style} data-testid="deep-dive-enemy-tooltip">
      <div className="flex items-center gap-2 mb-1">
        <Skull className="h-3.5 w-3.5 text-red-300" />
        <span className="text-white text-sm font-semibold">{enemy.name}</span>
        <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-white/50 ml-auto">{enemy.rarity}</span>
      </div>
      <div className="grid grid-cols-4 gap-1 mb-1.5">
        <MiniStat label="HP" v={`${enemy.hp_current}/${enemy.hp}`} color="text-red-300" />
        <MiniStat label="ATK" v={enemy.atk} color="text-orange-300" />
        <MiniStat label="DEF" v={enemy.def ?? 0} color="text-sky-300" />
        <MiniStat label="SPD" v={enemy.spd ?? 0} color="text-emerald-300" />
      </div>
      {enemy.element && (
        <div className="flex items-center gap-2 mb-1.5">
          <span className="font-mono text-[9px] uppercase tracking-[0.24em] text-white/50">Element</span>
          <ElementBadge element={enemy.element} />
        </div>
      )}
      {resistArray.length > 0 && <ResistGrid resist={resistArray} size="sm" />}
    </div>
  );
}

function MiniStat({ label, v, color }) {
  return (
    <div className="rounded bg-black/50 border border-white/10 px-1 py-0.5">
      <div className={`font-mono text-[8px] uppercase tracking-[0.18em] ${color}`}>{label}</div>
      <div className="text-white text-xs font-semibold leading-tight">{v}</div>
    </div>
  );
}

function AttackPicker({ enemy, arena, cooldowns, pending, onCancel, onPick }) {
  const moves = arena?.moves || [{ id: "strike", name: "Strike", mult: 1.0, cd: 0, element: "kinetic", kind: "damage" }];
  const atk = arena?.atk || 20;
  const armorPen = arena?.mods?.armor_pen || 0;
  const effDef = Math.max(0, Math.floor((enemy.def || 0) * (1 - armorPen)));

  const estimate = (m) => {
    if (m.kind !== "damage") return null;
    const base = Math.max(1, Math.floor(atk * (m.mult || 1)) - effDef);
    const resistPct = (enemy.resist || {})[m.element] || 0;
    return Math.max(0, Math.round(base * (1 - resistPct / 100)));
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onCancel} data-testid="deep-dive-attack-picker">
      <div className="rounded-2xl border border-cyan-300/40 bg-neutral-950 p-4 max-w-lg w-full shadow-2xl"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-cyan-300">Engage</div>
            <div className="text-white text-lg font-semibold flex items-center gap-2">
              <Skull className="h-4 w-4 text-red-300" /> {enemy.name}
              <span className="text-white/40 text-xs font-normal">
                HP {enemy.hp_current}/{enemy.hp}
              </span>
            </div>
          </div>
          <button onClick={onCancel} className="text-white/50 hover:text-white" data-testid="deep-dive-attack-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Enemy resistance strip — uses the site-wide ResistGrid */}
        <div className="mb-3 pb-2 border-b border-white/10 space-y-2">
          {enemy.element && (
            <div className="flex items-center gap-2 text-[10px] text-white/60">
              <span className="font-mono uppercase tracking-[0.22em]">Element</span>
              <ElementBadge element={enemy.element} />
            </div>
          )}
          <ResistGrid resist={Object.entries(enemy.resist || {}).map(([element, pct]) => ({ element, pct }))} size="sm" />
        </div>

        <div className="grid gap-1.5 max-h-[50vh] overflow-y-auto">
          {moves.map((m) => {
            const cd = cooldowns[m.id] || 0;
            const disabled = cd > 0 || pending;
            const est = estimate(m);
            const resistPct = (enemy.resist || {})[m.element] || 0;
            const noteCls = resistPct > 0 ? "text-red-300/70" : resistPct < 0 ? "text-emerald-300" : "text-white/50";
            return (
              <button key={m.id} onClick={() => !disabled && onPick(m)} disabled={disabled}
                data-testid={`deep-dive-attack-move-${m.id}`}
                className={`w-full text-left rounded-lg border p-2.5 transition-colors ${
                  disabled
                    ? "border-white/5 bg-white/[0.02] opacity-50 cursor-not-allowed"
                    : "border-white/15 bg-black/40 hover:border-cyan-300/50 hover:bg-cyan-400/[0.05]"
                }`}>
                <div className="flex items-center gap-2">
                  <ElementBadge element={m.element} />
                  <span className="text-white text-sm font-medium flex-1 truncate">{m.name}</span>
                  {cd > 0 && <span className="font-mono text-[10px] text-red-300">cooling · {cd}</span>}
                  {m.kind === "damage" && est !== null && (
                    <span className={`font-mono text-[11px] ${noteCls}`}>
                      ~{est} dmg
                      {resistPct > 0 && ` (−${resistPct}%)`}
                      {resistPct < 0 && ` (+${-resistPct}%)`}
                    </span>
                  )}
                  {m.kind === "shield" && (
                    <span className="font-mono text-[11px] text-emerald-300">heal</span>
                  )}
                </div>
                {m.desc && <div className="text-[11px] text-white/50 mt-0.5 truncate">{m.desc}</div>}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function LegendChip({ color, label, icon: Icon }) {
  return (
    <div className="inline-flex items-center gap-1.5">
      <span className="inline-block w-3 h-3 rounded-sm" style={{ background: color }} />
      {Icon && <Icon className="h-3 w-3" />}
      {label}
    </div>
  );
}

function BossCutIn({ boss, depth, onClose }) {
  // Auto-dismiss after 4.5s so the player can just breathe past it if they
  // don't hit Continue.
  useEffect(() => {
    const t = setTimeout(onClose, 4500);
    return () => clearTimeout(t);
  }, [onClose]);
  const resistArray = Object.entries(boss.resist || {}).map(([element, pct]) => ({ element, pct }));
  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur flex items-center justify-center p-4 animate-in fade-in duration-300"
      onClick={onClose} data-testid="deep-dive-boss-cutin">
      <div className="max-w-xl w-full text-center" onClick={(e) => e.stopPropagation()}>
        <div className="font-mono text-[11px] uppercase tracking-[0.5em] text-amber-300 animate-pulse mb-3">
          ⚔ BOSS ENCOUNTER
        </div>
        <div className="text-white/60 font-mono text-[10px] uppercase tracking-[0.32em] mb-1">
          {boss.title || `Depth ${depth}`}
        </div>
        <h2 className="font-display text-5xl sm:text-6xl text-white tracking-[0.02em] mb-3"
          style={{ textShadow: "0 0 32px rgba(253,224,71,0.55)" }}>
          {boss.name}
        </h2>
        {boss.flavor && (
          <p className="text-amber-100/70 italic text-sm mb-6 max-w-md mx-auto">{boss.flavor}</p>
        )}
        <div className="rounded-2xl border border-amber-400/40 bg-black/60 p-4 mx-auto max-w-md">
          <div className="grid grid-cols-4 gap-2 mb-3">
            <MiniStat label="HP" v={boss.hp} color="text-red-300" />
            <MiniStat label="ATK" v={boss.atk} color="text-orange-300" />
            <MiniStat label="DEF" v={boss.def ?? 0} color="text-sky-300" />
            <MiniStat label="SPD" v={boss.spd ?? 0} color="text-emerald-300" />
          </div>
          {boss.element && (
            <div className="flex items-center justify-center gap-2 mb-2">
              <span className="font-mono text-[9px] uppercase tracking-[0.24em] text-white/50">Element</span>
              <ElementBadge element={boss.element} />
            </div>
          )}
          {resistArray.length > 0 && <ResistGrid resist={resistArray} size="sm" />}
        </div>
        <Button onClick={onClose} data-testid="deep-dive-boss-cutin-close"
          className="mt-5 bg-amber-400 text-black hover:bg-amber-300">
          Face them
        </Button>
      </div>
    </div>
  );
}


function NPCMenu({ npc, actions, pending, onCancel, onPick }) {
  const meta = NPC_ROLE_META[npc.key] || { icon: UserPlus, color: "#a3a3a3", label: npc.role };
  const Icon = meta.icon;
  return (
    <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onCancel} data-testid="deep-dive-npc-menu">
      <div className="rounded-2xl border p-4 max-w-md w-full shadow-2xl bg-neutral-950"
        style={{ borderColor: meta.color + "70" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Icon className="h-5 w-5" style={{ color: meta.color }} />
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.32em]" style={{ color: meta.color }}>{npc.role}</div>
              <div className="text-white text-lg font-semibold">{npc.name}</div>
            </div>
          </div>
          <button onClick={onCancel} className="text-white/50 hover:text-white" data-testid="deep-dive-npc-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        {npc.blurb && <p className="text-white/60 text-xs mb-3 italic">{npc.blurb}</p>}
        <div className="grid gap-1.5">
          {actions.length === 0 && (
            <div className="text-white/40 text-xs text-center py-4">Nothing more to offer.</div>
          )}
          {actions.map((a) => {
            const disabled = !a.enabled || pending;
            return (
              <button key={a.id} disabled={disabled}
                onClick={() => onPick(a)}
                data-testid={`deep-dive-npc-action-${a.id}`}
                className={`w-full text-left rounded-lg border p-2.5 transition-colors ${
                  disabled
                    ? "border-white/5 bg-white/[0.02] opacity-50 cursor-not-allowed"
                    : "border-white/15 bg-black/40 hover:bg-white/[0.06]"
                }`}
                style={!disabled ? { borderColor: meta.color + "60" } : {}}>
                <div className="flex items-center gap-2">
                  <span className="text-white text-sm flex-1">{a.label}</span>
                  {a.cost_credits > 0 && (
                    <span className="font-mono text-[11px] text-amber-300">
                      <Coins className="inline h-3 w-3 mr-0.5" />{a.cost_credits}
                    </span>
                  )}
                  {a.cost_materials && (
                    <span className="font-mono text-[11px] text-white/60">
                      <Gem className="inline h-3 w-3 mr-0.5" />{a.cost_materials.qty}× T{a.cost_materials.tier}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
